/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// react-router components
import { Routes, Route, Navigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import logo from "assets/images/logo-fisher-dugmore-financial.png";

// Constants
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  TOKEN: 'token',
};

const ROUTES = {
  ROOT: '/',
  CLIENTS: '/clients',
  USERS: '/users',
  CONTRACT_TEMPLATES: '/contract-templates',
  SIGN_IN: '/authentication/sign-in',
  LOGIN_REQUEST: '/authentication/login-request',
};

const PUBLIC_ROUTES = [ROUTES.SIGN_IN, ROUTES.LOGIN_REQUEST];
const VALID_ROUTES = [ROUTES.ROOT, ROUTES.CLIENTS, ROUTES.USERS, ROUTES.CONTRACT_TEMPLATES, ...PUBLIC_ROUTES];

const REDIRECT_DELAY = 2000; // 2 seconds

const NOT_FOUND_STYLES = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  color: '#776F64',
  fontFamily: 'inherit',
  background: '#f7f6f4'
};

/**
 * NotFound component for handling 404 errors
 */
function NotFound() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(ROUTES.SIGN_IN, { replace: true });
    }, REDIRECT_DELAY);
    
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div style={NOT_FOUND_STYLES}>
      <img src={logo} alt="Fisher Dugmore Financial" style={{ width: 120, marginBottom: 32 }} />
      <h1 style={{ fontSize: 48, margin: 0 }}>404</h1>
      <h2 style={{ fontWeight: 400, margin: '16px 0 8px 0' }}>Page Not Found</h2>
      <p style={{ fontSize: 18, margin: 0 }}>The page you are looking for does not exist.</p>
      <p style={{ fontSize: 16, color: '#aaa', marginTop: 24 }}>Redirecting to login...</p>
    </div>
  );
}

/**
 * Get authentication token from localStorage
 * @returns {string|null} The authentication token
 */
function getAuthToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || localStorage.getItem(STORAGE_KEYS.TOKEN);
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has valid token
 */
function isAuthenticated() {
  return !!getAuthToken();
}

/**
 * Main App component
 */
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  // Helper to determine if the current route is a 404
  const is404 = !routes.some(route => route.route === pathname);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Handle authentication redirects
  useEffect(() => {
    const isPublicRoute = PUBLIC_ROUTES.includes(pathname);
    
    // Only redirect if we're not already on a public route
    if (!isAuthenticated() && !isPublicRoute) {
      navigate(ROUTES.SIGN_IN, { replace: true });
    }
  }, [pathname, navigate]);

  // Handle client-side routing for SPA
  useEffect(() => {
    // If we're on a route that doesn't exist, redirect appropriately
    if (!VALID_ROUTES.includes(pathname) && pathname !== ROUTES.ROOT) {
      if (isAuthenticated()) {
        // User is authenticated, redirect to main page
        navigate(ROUTES.CLIENTS, { replace: true });
      } else {
        // User is not authenticated, redirect to sign-in
        navigate(ROUTES.SIGN_IN, { replace: true });
      }
    }
  }, [pathname, navigate]);

  /**
   * Generate routes recursively
   * @param {Array} allRoutes - Array of route objects
   * @returns {Array} Array of Route components
   */
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && !is404 && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode)}
              brandName="FD Contracts"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          </>
        )}
        <Routes>
          <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.CLIENTS} replace />} />
          {getRoutes(routes)}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && !is404 && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode)}
            brandName="FD Contracts"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.CLIENTS} replace />} />
        {getRoutes(routes)}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ThemeProvider>
  );
}
